/**
 * Ticket Detail Component
 * Shows full conversation and allows technician to reply
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, ChevronDown, ChevronLeft, Circle, CircleDot, CornerUpLeft, Lock, Mic, Paperclip, SendHorizontal, Trash2, X, Zap, type LucideIcon } from 'lucide-react';
import { useTickets } from '../../hooks/useTickets';
import { useTicketWebSocket } from '../../hooks/useTicketWebSocket';
import { useBridge } from '../../hooks/useBridge';
import { useAuth } from '../../hooks/useAuth';
import { useHoldVoiceRecorder } from '../../hooks/useHoldVoiceRecorder';
import { MessageList } from './MessageList';
import { VoiceSpeedProvider } from '../chat/VoiceSpeedContext';
// SourceBanner import kept intentionally — component is not rendered here (replaced by
// per-divider inline render in MessageList) but the file is preserved for future reuse.
import { SourceBanner } from './SourceBanner';
// Reference prevents TS6133 "declared but never read" while keeping the import visible for grep.
void SourceBanner;
import { CategoryModal } from './CategoryModal';
import { CannedResponsePicker } from './CannedResponsePicker';
import { ShadowSuggestions } from './ShadowSuggestions';
import { MediaPicker } from '../media/MediaPicker';
import { apiClient } from '../../services/api';
import type { ExternalContext } from '../../services/api';
import type { Attachment, Message, ServiceType, Ticket } from '../../types';
import { MessageActionModal } from '../chat/MessageActionModal';
import { MessageDeleteSheet } from '../chat/MessageDeleteSheet';
import { ReplyContextBar, EditContextBar } from '../chat';

/** Horizontal slide distance (px) before pointerup is interpreted as cancel. */
const VOICE_SLIDE_CANCEL_PX = 80;
/** Vertical slide distance (px) before pointerup is interpreted as lock. */
const VOICE_SLIDE_LOCK_PX = 80;

function formatVoiceDuration(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

interface TicketDetailProps {
  ticketId: string;
  /** Row from list/dashboard — seeds header immediately and skips full-screen ticket spinner when ids match. */
  listTicket?: Ticket | null;
  onBack: () => void;
  /** Phase 34: navigate to a different ticket from inside this view.
   *  Triggered after a successful L2 reopen so the user lands in the
   *  just-reopened ticket's own single-ticket detail (active layout,
   *  composer enabled, "Close" button instead of the closed-bar) rather
   *  than staying in a stale multi-ticket conversation view. Optional —
   *  callers that don't need cross-ticket navigation (e.g. embed surfaces)
   *  pass undefined and the reopen path falls back to an in-place refresh. */
  onSelectTicket?: (ticket: Ticket) => void;
  /** L2-only: open the canned-response admin library from the ⚡ picker's
   *  Manage button. Undefined for level<2 (hides the button); the real gate
   *  is server-side requireLevel(2). */
  onOpenAdmin?: () => void;
}

export function TicketDetail({ ticketId, listTicket, onBack, onSelectTicket, onOpenAdmin }: TicketDetailProps) {
  const {
    currentTicket: ticket,
    isLoadingTicket,
    isSending,
    error: sendError,
    loadTicket,
    loadConversationInitial,
    loadConversationOlder,
    mergeConversationLatestTail,
    conversationMessages,
    conversationHasMore,
    conversationLoadingInitial,
    conversationLoadingOlder,
    sendReply,
    sendReplyWithAttachments,
    editMessage,
    markAsRead,
    closeTicket,
    closeTicketsBatch,
    loadUserActiveTickets,
    userActiveTickets,
    refreshOpenTickets,
    loadStats,
    categories,
    loadCategories,
    serviceTypes,
    loadServiceTypes,
    beginTicketDetailSession,
    hydrateTicketFromListRow,
  } = useTickets();

  const { isInSuperApp } = useBridge();
  // Phase 34: gate the L2-only Reopen button on the authed user's level.
  // useAuth surfaces `level` straight from the JWT payload via /verify; an L1
  // session (level === 1) never sees the button rendered. Backend enforces
  // requireLevel(2) regardless, so this is purely a UX hide.
  const { user: authUser } = useAuth();
  const isLevel2 = (authUser?.level ?? 1) >= 2;

  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  // Phase 34: reopen modal state. `reopenTargetTicketId` lets the modal
  // target ANY ticket in the conversation (not just `props.ticketId`) —
  // long-pressing a closed TICK-XXXXX divider sets this; the bottom-bar
  // Reopen button (single-ticket detail view) leaves it null so the modal
  // falls back to `ticket.ticketId`.
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenTargetTicketId, setReopenTargetTicketId] = useState<string | null>(null);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenSubmitting, setReopenSubmitting] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  // Phase 37: canned-responses "⚡" picker. Opening it shows a categorized,
  // searchable sheet; tapping an answer inserts its body into the composer.
  const [showCannedPicker, setShowCannedPicker] = useState(false);
  // Reply-target picker: when the user has >1 open ticket in this unified
  // conversation, the composer is bound to the OPENED ticket. This menu lets
  // the agent switch which open ticket they're answering — selecting one
  // re-opens it via onSelectTicket so the composer, the quick-reply
  // suggestions, and the conversation all follow consistently (no send-path
  // change: we reuse the existing per-ticket navigation/session pinning).
  const [showReplyTargetMenu, setShowReplyTargetMenu] = useState(false);

  // Multi-select close: when active, ticket dividers in MessageList show
  // checkboxes; the Close button switches to "Close N selected" and dispatches
  // the batched API. Pre-seeded with the currently-focused ticket so the
  // common single-ticket case is "tap Select → tap Close → done".
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<string>>(new Set());

  // v4.1 — message multi-select. Parallel to (and independent of) the
  // ticket-divider selection above. The two modes can coexist in state
  // but only one is ever entered at a time from the user's perspective
  // (entry points are different gestures: long-press divider → tickets,
  // long-press bubble → messages).
  const [messageSelectionMode, setMessageSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<number>>(new Set());
  /** Phase 33 — when set, the composer renders a quoted-preview bar
   *  above the input identifying this Message as the reply target.
   *  Cleared on send-success, cancel, ticket switch, and on send-fail
   *  (per CONTEXT.md acceptance criterion #3). State lives here, not
   *  in a Context — matches the attachments / messageSelectionMode /
   *  activeDeleteModal pattern. Plan 33-05 renders the bar; plan 33-06
   *  threads replyToMessageId into the send call. */
  const [replyTarget, setReplyTarget] = useState<Message | null>(null);
  /** Edit-message phase — message currently being edited. When set, the
   *  composer renders an EditContextBar above and the send button does
   *  a PATCH instead of a POST. Cleared on send-success, cancel, ticket
   *  switch, and on send-fail (parallels replyTarget). Mutually exclusive
   *  with replyTarget — setting one clears the other. */
  const [editTarget, setEditTarget] = useState<Message | null>(null);
  /** Modal state. activeDeleteModal holds the single-message context plus
   *  the held bubble's bounding rect, its outerHTML at gesture time (used
   *  to render a faithful clone above the backdrop, Telegram-style), and
   *  the isMine side flag (controls which edge the menu anchors to).
   *  activeDeleteSheet holds the batch about to be deleted. */
  const [activeDeleteModal, setActiveDeleteModal] = useState<{
    message: Message;
    anchorRect: DOMRect;
    bubbleHtml: string;
    isMine: boolean;
  } | null>(null);
  const [activeDeleteSheet, setActiveDeleteSheet] = useState<Message[] | null>(null);

  // Phase 32 inline-divider: per-ticket external context state.
  // ticketsExternal: map of ticketId → ExternalContext (populated by batch fetch).
  // refreshingTicketIds: Set of ticketIds currently being refreshed (spinner state).
  // failedRefreshTicketIds: Set of ticketIds where last refresh attempt failed (warning icon).
  const [ticketsExternal, setTicketsExternal] = useState<Record<string, ExternalContext>>({});
  const [refreshingTicketIds, setRefreshingTicketIds] = useState<Set<string>>(new Set());
  const [failedRefreshTicketIds, setFailedRefreshTicketIds] = useState<Set<string>>(new Set());

  const MAX_FILE_SIZE_MB = 20;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const BLOCKED_EXTENSIONS = new Set([
    '.exe', '.msi', '.dll', '.com', '.scr', '.pif', '.cpl',
    '.bat', '.cmd', '.sh', '.bash', '.zsh', '.fish', '.command',
    '.vbs', '.vbe', '.wsf', '.wsh', '.ps1', '.psm1', '.psd1',
    '.svg',
    '.html', '.htm', '.xhtml', '.hta', '.shtml',
    '.js', '.mjs', '.cjs', '.jsx', '.ts', '.tsx',
    '.php', '.php3', '.php4', '.php5', '.php7', '.phtml',
    '.asp', '.aspx', '.cshtml',
    '.py', '.pyc', '.pyo', '.rb', '.pl', '.cgi',
    '.jar', '.class', '.war', '.ear',
    '.app', '.pkg', '.dmg',
    '.deb', '.rpm',
    '.reg', '.inf', '.lnk', '.url',
    '.so', '.dylib',
  ]);

  const checkFileAllowed = (fileName: string): string | null => {
    const ext = fileName.includes('.')
      ? `.${fileName.split('.').pop()!.toLowerCase()}`
      : '';
    if (ext && BLOCKED_EXTENSIONS.has(ext)) {
      return `File type "${ext}" is not allowed.`;
    }
    return null;
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Ref points at a contentEditable <div> — see the composer JSX below for
  // why we don't use a <textarea>. The auto-grow / IME logic below treats this
  // as a generic HTMLElement.
  const messageInputRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const pendingScrollRestoreRef = useRef<{ prevHeight: number; prevTop: number } | null>(null);
  const initialScrollDoneForTicketRef = useRef<string | null>(null);
  /** First WS `onOpen` after a ticket session starts — bootstrap already fetched tail; skip duplicate merge. */
  const wsSkipFirstOpenRef = useRef(true);

  /**
   * Pixels from bottom of the scroll area to consider "following the tail".
   * Hysteresis: once pinned, stay pinned until this far from bottom so a new bubble
   * (or scroll event after scrollHeight grows) does not flip us off the tail.
   */
  const NEAR_BOTTOM_PIN_PX = 160;
  const NEAR_BOTTOM_UNPIN_PX = 520;

  /** Max height ~7 lines; then scroll inside the field (matches prior max-h-28) */
  const MESSAGE_INPUT_MAX_PX = 112;

  /** True while an IME (Khmer, Chinese, Japanese, Korean, even iOS autocorrect)
   *  is mid-composition. We must NOT touch the textarea's `height` during this
   *  window — the IME anchors its cursor / composition popup to the field's
   *  layout box, and the auto-grow's `height='auto' → scrollHeight` two-step
   *  invalidates that anchor on every keystroke. Symptom on iOS Khmer: cursor
   *  gets stuck, can't be moved with the spacebar trackpad, deletes misfire. */
  const isComposingRef = useRef(false);

  const syncMessageInputHeight = useCallback(() => {
    if (isComposingRef.current) return;
    const el = messageInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MESSAGE_INPUT_MAX_PX)}px`;
  }, []);

  /** Jump to latest message; keeps near-bottom tracking in sync after programmatic scroll. */
  const scrollConversationToBottomInstant = useCallback(() => {
    const root = scrollAreaRef.current;
    if (!root) return;
    root.scrollTop = root.scrollHeight;
    messagesEndRef.current?.scrollIntoView({ block: 'end', behavior: 'auto' });
    nearBottomRef.current = true;
  }, []);

  /** Recompute tail-follow pin from live layout (used after new messages / media growth). */
  const syncNearBottomFromDom = useCallback(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (dist < NEAR_BOTTOM_PIN_PX) nearBottomRef.current = true;
    else if (dist > NEAR_BOTTOM_UNPIN_PX) nearBottomRef.current = false;
  }, []);

  // Get auth token from localStorage
  const token = localStorage.getItem('support_auth_token') || '';

  // WebSocket for real-time ticket updates
  // onOpen fires on every reconnect — catches up on messages missed during the gap
  const { isConnected: wsConnected, lastMessage: wsMessage } = useTicketWebSocket({
    ticketId,
    authToken: token,
    enabled: true,
    onOpen: () => {
      if (wsSkipFirstOpenRef.current) {
        wsSkipFirstOpenRef.current = false;
        return;
      }
      void mergeConversationLatestTail(ticketId);
    },
  });

  // One session per ticket: clear conversation, optionally seed header from list row, reset scroll markers.
  useLayoutEffect(() => {
    wsSkipFirstOpenRef.current = true;
    beginTicketDetailSession(ticketId);
    if (listTicket?.ticketId === ticketId) {
      hydrateTicketFromListRow(listTicket);
    }
    initialScrollDoneForTicketRef.current = null;
    nearBottomRef.current = true;
  }, [ticketId, listTicket, beginTicketDetailSession, hydrateTicketFromListRow]);

  // Ticket metadata + first page of messages in parallel (no "wait for ticket then fetch messages" waterfall).
  useEffect(() => {
    void loadTicket(ticketId);
    loadCategories();
    loadServiceTypes();
    void loadConversationInitial(ticketId);
  }, [ticketId, loadTicket, loadCategories, loadServiceTypes, loadConversationInitial]);

  // Polling fallback when WS is not connected — catches up during reconnect gaps
  useEffect(() => {
    if (wsConnected) return;
    const intervalId = setInterval(() => {
      void loadTicket(ticketId).then(() => mergeConversationLatestTail(ticketId));
    }, 5000);
    return () => clearInterval(intervalId);
  }, [ticketId, wsConnected, loadTicket, mergeConversationLatestTail]);

  // Safety-net heartbeat even when WS is connected — covers the scenario where
  // the DO wakes up for notifyUpdate but ctx.getWebSockets() returns 0 (no sockets
  // registered), meaning the push was silently lost. 30s poll catches the miss.
  useEffect(() => {
    if (!wsConnected) return;
    const intervalId = setInterval(() => {
      loadTicket(ticketId);
    }, 30000);
    return () => clearInterval(intervalId);
  }, [ticketId, wsConnected, loadTicket]);

  // Refresh when new message or ticket update received via WebSocket
  useEffect(() => {
    if (wsMessage && (wsMessage.type === 'new_message' || wsMessage.type === 'ticket_updated')) {
      void loadTicket(ticketId).then(() => mergeConversationLatestTail(ticketId));
    }
  }, [wsMessage, ticketId, loadTicket, mergeConversationLatestTail]);

  // v4.1 — message_deleted fires when ANY technician (including this one
  // on another device) soft-deletes one or more messages on a ticket in
  // this conversation. Every delete is now "Delete for everyone" — rows
  // are replaced with the deleted-placeholder bubble. Same refresh path
  // as new_message — simpler than mutating local state in place, and the
  // server is the source of truth for what's visible.
  useEffect(() => {
    if (wsMessage && wsMessage.type === 'message_deleted') {
      void mergeConversationLatestTail(ticketId);
    }
  }, [wsMessage, ticketId, mergeConversationLatestTail]);

  // edit-message phase — message_edited fires when any tech edits a row
  // (including this tech on another device). Refresh the conversation
  // tail so the new text appears. Cheap because we already optimistically
  // applied the update in `editMessage` when WE were the editor — the
  // refresh is a no-op in that case but covers cross-device sync.
  useEffect(() => {
    if (wsMessage && wsMessage.type === 'message_edited') {
      void mergeConversationLatestTail(ticketId);
    }
  }, [wsMessage, ticketId, mergeConversationLatestTail]);

  // `new_ticket` lands when the end-user opens a fresh /start <ref> deeplink
  // while the technician is already on their conversation screen. The server
  // emits it on the `user_<userId>` room (which the WS auto-joins on connect),
  // so the event reaches us — but the old handler above ignored it. As a
  // result the divider for the brand-new ticket arrived through the next
  // conversation poll AND got mis-classified as "closed" (grey chip) because
  // userActiveTickets was still the pre-creation snapshot.
  //
  // On every new_ticket:
  //  1. Refresh the unified conversation so the new ticket's
  //     REFERRAL_INIT placeholder + divider show up immediately.
  //  2. Refresh userActiveTickets so the selectable-set includes the new id
  //     before the next render — divider renders in its correct "open" blue
  //     on the first frame instead of flashing grey first.
  useEffect(() => {
    if (!wsMessage || wsMessage.type !== 'new_ticket') return;
    void mergeConversationLatestTail(ticketId);
    if (ticket?.userId) {
      void loadUserActiveTickets(Number(ticket.userId));
    }
  }, [wsMessage, ticketId, ticket?.userId, mergeConversationLatestTail, loadUserActiveTickets]);

  const conversationTailSignature = useMemo(() => {
    const last = conversationMessages[conversationMessages.length - 1];
    if (!last) return '';
    return `${last.id}|${last.mediaUrl ?? ''}|${last.timestamp}|${last.from}`;
  }, [conversationMessages]);

  // Mark as read when ticket loads
  useEffect(() => {
    if (ticket?.hasUnreadMessages) {
      markAsRead(ticketId);
    }
  }, [ticket, ticketId, markAsRead]);

  // Phase 32 inline-divider: per-ticket on-demand refresh.
  // Called when the user taps the refresh icon on a specific ticket's Ref chip.
  // On success: updates ticketsExternal map for that ticketId with fresh server data.
  // On failure: adds to failedRefreshTicketIds (warning icon shows for that ticket).
  const handleRefreshTicketExternal = useCallback(async (targetTicketId: string) => {
    setRefreshingTicketIds(prev => new Set(prev).add(targetTicketId));
    setFailedRefreshTicketIds(prev => {
      const next = new Set(prev);
      next.delete(targetTicketId);
      return next;
    });
    try {
      const result = await apiClient.refreshExternalContext(targetTicketId);
      if (result.success && result.data?.success) {
        const t = result.data.ticket;
        if (t.externalSource && t.externalReferenceId) {
          setTicketsExternal(prev => ({
            ...prev,
            [targetTicketId]: {
              externalReferenceId: t.externalReferenceId!,
              externalSource: t.externalSource!,
              externalTitle: t.externalTitle ?? null,
              externalDescriptionMd: t.externalDescriptionMd ?? null,
              externalFetchedAt: t.externalFetchedAt ?? new Date().toISOString(),
            },
          }));
        }
      } else {
        setFailedRefreshTicketIds(prev => new Set(prev).add(targetTicketId));
      }
    } catch {
      setFailedRefreshTicketIds(prev => new Set(prev).add(targetTicketId));
    } finally {
      setRefreshingTicketIds(prev => {
        const next = new Set(prev);
        next.delete(targetTicketId);
        return next;
      });
    }
  }, []);

  // Phase 32 inline-divider: batch-fetch external metadata whenever the
  // conversation messages list changes. Avoids N+1 per-divider calls.
  // We intentionally exclude ticketsExternal from deps to prevent an infinite
  // loop (fetch succeeds → state updates → effect re-fires).
  useEffect(() => {
    const uniqueIds = Array.from(
      new Set(conversationMessages.map(m => m.ticketId).filter((id): id is string => !!id))
    );
    if (uniqueIds.length === 0) return;
    const missing = uniqueIds.filter(id => !ticketsExternal[id]);
    if (missing.length === 0) return;
    void apiClient.getTicketsExternal(uniqueIds).then(res => {
      if (res.success && res.data) {
        setTicketsExternal(prev => ({ ...prev, ...res.data!.tickets }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationMessages]);

  // Scroll to bottom instantly (before first paint) once the initial page loads.
  // Must use scrollTop directly — scroll-behavior on the container is set to 'auto'
  // so there is no animation, meaning the user never sees a downward scroll.
  useLayoutEffect(() => {
    if (conversationLoadingInitial || conversationMessages.length === 0) return;
    if (initialScrollDoneForTicketRef.current === ticketId) return;
    initialScrollDoneForTicketRef.current = ticketId;
    scrollConversationToBottomInstant();
    requestAnimationFrame(() => {
      scrollConversationToBottomInstant();
      requestAnimationFrame(() => {
        scrollConversationToBottomInstant();
      });
    });
  }, [
    ticketId,
    conversationLoadingInitial,
    conversationMessages.length,
    scrollConversationToBottomInstant,
  ]);

  // Keep the view pinned to bottom while media/layout finishes, but only if the
  // user is already near the bottom (Messenger-style behavior).
  useEffect(() => {
    const root = scrollAreaRef.current;
    if (!root) return;
    if (initialScrollDoneForTicketRef.current !== ticketId) return;

    const ro = new ResizeObserver(() => {
      syncNearBottomFromDom();
      if (!nearBottomRef.current) return;
      // Use instant jump; smooth is handled only for new message tails.
      root.scrollTop = root.scrollHeight;
      messagesEndRef.current?.scrollIntoView({ block: 'end', behavior: 'auto' });
      nearBottomRef.current = true;
    });

    // Observe the scroll content box (first child) if present, else the root.
    const content = root.firstElementChild ?? root;
    ro.observe(content);
    return () => ro.disconnect();
  }, [ticketId, conversationMessages.length, conversationLoadingInitial, syncNearBottomFromDom]);

  // When the tail changes (new / updated last row), scroll so the latest row is visible.
  // Inbound Telegram messages (`from === 'user'`) always jump to bottom — support must see
  // new client text even if pin state / initial-scroll ref is briefly wrong.
  // Other tail updates (e.g. technician reply, sticker URL filled) follow only when pinned.
  useLayoutEffect(() => {
    if (!conversationTailSignature) return;
    const root = scrollAreaRef.current;
    if (!root) return;

    const inboundFromClient = conversationTailSignature.endsWith('|user');

    if (!inboundFromClient && initialScrollDoneForTicketRef.current !== ticketId) return;

    if (inboundFromClient) {
      scrollConversationToBottomInstant();
      requestAnimationFrame(() => {
        scrollConversationToBottomInstant();
        requestAnimationFrame(() => {
          scrollConversationToBottomInstant();
        });
      });
      return;
    }

    syncNearBottomFromDom();
    if (!nearBottomRef.current) return;
    scrollConversationToBottomInstant();
    requestAnimationFrame(() => {
      scrollConversationToBottomInstant();
    });
  }, [conversationTailSignature, ticketId, syncNearBottomFromDom, scrollConversationToBottomInstant]);

  // Preserve scroll position when prepending older messages.
  // Must be instant (no animation) so the viewport doesn't jump.
  useLayoutEffect(() => {
    const pr = pendingScrollRestoreRef.current;
    if (!pr) return;
    pendingScrollRestoreRef.current = null;
    const root = scrollAreaRef.current;
    if (!root) return;
    root.scrollTop = pr.prevTop + (root.scrollHeight - pr.prevHeight);
  }, [conversationMessages]);

  // Set of TICK-XXXX dividers the user is allowed to toggle. The conversation
  // history may include long-since-closed tickets — those are rendered but
  // not selectable. userActiveTickets is the source of truth. Declared
  // here (instead of nearer its other consumers below) so the pagination
  // gate just below can reference it.
  const activeTicketIdSet = useMemo(() => {
    return new Set(userActiveTickets.map((t) => t.ticketId));
  }, [userActiveTickets]);

  // Load older messages when user scrolls to top.
  //
  // Bug fix: with `compactClosedTickets` on, every closed-ticket message
  // loaded by a pagination batch gets collapsed into the SAME pill at the
  // top. The scroll-restore math (prevTop + ΔscrollHeight) only moves the
  // viewport when the new content adds visible height — but a collapsed
  // batch adds ≈0 height, so the sentinel stays in the 80px rootMargin,
  // the observer re-fires, and we cascade through the user's entire
  // closed-ticket history before stopping (hasMore=false).
  //
  // Gate: once the topmost loaded message belongs to a closed ticket,
  // we've crossed the open/closed boundary and stop auto-paginating. The
  // user can still tap the collapsed pill to reveal the closed-ticket
  // messages already in memory; they're just not forced to load every
  // closed ticket from their account history every time they scroll up
  // in an active conversation.
  //
  // The gate ONLY applies when (a) the ticket being viewed is itself
  // open — otherwise we'd block normal pagination inside a closed ticket
  // the technician opened directly (e.g. from an archived list) — and
  // (b) the user hasn't yet expanded any closed-history pill. Expanding
  // a pill is an explicit opt-in to seeing older history, so we release
  // the gate and let scroll-driven pagination resume from there.
  const [hasOptedIntoClosedHistory, setHasOptedIntoClosedHistory] = useState(false);
  // Reset the opt-in when the user navigates to a different ticket —
  // each conversation gets a fresh "collapsed by default" experience.
  useEffect(() => {
    setHasOptedIntoClosedHistory(false);
  }, [ticketId]);

  // Phase 33 — clear any pending reply target when the user switches
  // tickets. A reply target captured on ticket A must not leak into the
  // composer of ticket B (composer would otherwise show a quoted bar for
  // a message that isn't part of this conversation). Mirrors the
  // setHasOptedIntoClosedHistory(false) reset above — same trigger
  // (ticketId change), same shape (single-state useEffect). Plan 33-05
  // will additionally clear on send-success and explicit cancel.
  useEffect(() => {
    setReplyTarget(null);
  }, [ticketId]);
  const currentTicketIsOpen =
    !!ticket && activeTicketIdSet.has(ticket.ticketId);
  const topmostLoadedMessage = conversationMessages[0];
  const topmostIsClosedTicket =
    currentTicketIsOpen &&
    !hasOptedIntoClosedHistory &&
    !!topmostLoadedMessage &&
    !!topmostLoadedMessage.ticketId &&
    !activeTicketIdSet.has(topmostLoadedMessage.ticketId);
  useEffect(() => {
    const root = scrollAreaRef.current;
    const target = topSentinelRef.current;
    if (!root || !target) return;

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          if (!conversationHasMore || conversationLoadingOlder) continue;
          if (topmostIsClosedTicket) continue;
          pendingScrollRestoreRef.current = {
            prevHeight: root.scrollHeight,
            prevTop: root.scrollTop,
          };
          void loadConversationOlder(ticketId);
        }
      },
      { root, rootMargin: '80px 0px 0px 0px', threshold: 0 }
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, [
    ticketId,
    conversationHasMore,
    conversationLoadingOlder,
    loadConversationOlder,
    conversationMessages.length,
    topmostIsClosedTicket,
  ]);

  const handleMessagesScroll = () => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (dist < NEAR_BOTTOM_PIN_PX) nearBottomRef.current = true;
    else if (dist > NEAR_BOTTOM_UNPIN_PX) nearBottomRef.current = false;
  };

  // The composer below uses a contentEditable <div> instead of <textarea>
  // specifically to suppress iOS's keyboard form accessory bar (the system
  // toolbar with ◀▶ / Done that auto-injects above any focused <input> /
  // <textarea> in WKWebView). contentEditable elements don't trigger it.
  //
  // Tradeoff of contentEditable: React no longer owns the DOM content
  // automatically — typing pushes into `message` via onInput, but when state
  // changes externally (e.g. setMessage('') after a successful send, OR
  // setMessage(<edit target's text>) on entering edit mode) the DOM needs
  // an explicit sync. The equality guard makes this a no-op during normal
  // typing (we just read FROM the DOM, so they match).
  //
  // Order matters: sync innerText FIRST, then measure scrollHeight for the
  // auto-grow. Splitting these into two useLayoutEffect hooks (the original
  // shape) caused the auto-grow to read scrollHeight while the DOM still
  // held the OLD text — most visible when entering edit mode with a multi-
  // line message, where the input collapsed to one line of height while
  // the text overflowed below.
  useLayoutEffect(() => {
    const el = messageInputRef.current;
    if (!el) return;
    if (el.innerText !== message) {
      el.innerText = message;
    }
    syncMessageInputHeight();
  }, [message, syncMessageInputHeight]);

  const handleSendMessage = async () => {
    if ((!message.trim() && attachments.length === 0) || isSending) return;
    setAttachmentError(null);

    // Edit-message phase — when editTarget is set, the send button means
    // "save edit", NOT "send new". Branch here before the reply path so
    // PATCH /tickets/.../messages/:id runs instead of POST /reply.
    // Attachments are ignored in edit mode (Telegram's editMessageText/
    // editMessageCaption can't change the underlying media — that's a
    // separate editMessageMedia API not scoped here). The UI should hide
    // the attach button while editing, but defending here is cheap.
    if (editTarget) {
      const currentEditTarget = editTarget;
      const trimmed = message.trim();
      if (trimmed.length === 0) {
        // Empty edit — refuse rather than POST (server would 400 anyway).
        setAttachmentError('Cannot save an empty edit.');
        return;
      }
      // Clear UI state BEFORE the await so the EditContextBar disappears
      // immediately on tap — matches the reply-bar UX.
      setEditTarget(null);
      const success = await editMessage(ticketId, currentEditTarget.id, trimmed);
      if (success) {
        setMessage('');
      } else {
        // Restore edit state on failure so the user can retry without
        // re-opening the modal + re-typing.
        setEditTarget(currentEditTarget);
        setAttachmentError(sendError || 'Failed to save edit. Please try again.');
      }
      return;
    }

    // Phase 33: snapshot replyTarget's DB id BEFORE clearing, then clear
    // FIRST so the quoted-preview bar disappears immediately on tap —
    // matches the attachment-preview UX. Per CONTEXT.md acceptance
    // criterion #3, sending the reply (success or failure) clears the
    // reply-target state, so a single clear-before-send covers both
    // branches. The id (NOT telegramMessageId) flows to the server,
    // which resolves the Telegram message_id server-side per plan 33-03.
    // Voice-only sends (handleVoiceRecordingComplete) intentionally do
    // NOT carry replyToMessageId — media uploads via /media don't yet
    // support reply_parameters (see api.ts JSDoc + 33-05-SUMMARY.md).
    const currentReplyTargetId = replyTarget?.id;
    if (replyTarget) setReplyTarget(null);

    const success =
      attachments.length > 0
        ? await sendReplyWithAttachments(ticketId, message.trim(), attachments, currentReplyTargetId)
        : await sendReply(ticketId, message.trim(), currentReplyTargetId);

    if (success) {
      setMessage('');
      setAttachments([]);
      // Phase 33: after a successful send, scroll the conversation to
      // its new bottom so the freshly-rendered bubble isn't clipped by
      // the composer / keyboard. Two rAFs because the optimistic-insert
      // path adds the bubble in the next React commit AND the DOM may
      // need a tick to settle when the bubble carries media or a
      // quoted-preview sub-element (variable height). nearBottomRef
      // pin gets re-set inside scrollConversationToBottomInstant so
      // future incoming messages continue to tail-follow.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollConversationToBottomInstant();
        });
      });
    } else {
      setAttachmentError(sendError || 'Failed to send. Please try again.');
    }
  };

  const handleAddAttachment = (attachment: Attachment) => {
    if (attachment.fileName) {
      const typeError = checkFileAllowed(attachment.fileName);
      if (typeError) {
        setAttachmentError(typeError);
        setShowMediaPicker(false);
        return;
      }
    }
    const sizeBytes = Math.ceil((attachment.base64.length * 3) / 4);
    if (sizeBytes > MAX_FILE_SIZE_BYTES) {
      setAttachmentError(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
      setShowMediaPicker(false);
      return;
    }
    setAttachmentError(null);
    setAttachments(prev => [...prev, attachment]);
    setShowMediaPicker(false);
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Send a finished voice recording directly — bypasses the textarea pipeline.
  const handleVoiceRecordingComplete = useCallback(
    async (attachment: Attachment) => {
      await sendReplyWithAttachments(ticketId, '', [attachment]);
    },
    [sendReplyWithAttachments, ticketId],
  );

  // Telegram-style press-and-hold voice recorder. Hooks into the same bridge
  // microphone.* surface the old modal used, but exposes imperative hold
  // controls so the chat-input row can drive the state machine directly.
  const voice = useHoldVoiceRecorder({ onComplete: handleVoiceRecordingComplete });
  const isRecordingVoice = voice.state === 'recording' || voice.state === 'processing';

  // Pointer-capture state for the press-and-hold mic.
  // - Slide LEFT past VOICE_SLIDE_CANCEL_PX → cancel.
  // - Slide UP past VOICE_SLIDE_LOCK_PX → lock the recording (hands-free mode);
  //   from there the mic button swaps for a Send button and the user can stop
  //   holding while recording continues.
  // - Release within both thresholds → send normally.
  const micButtonRef = useRef<HTMLButtonElement>(null);
  const voicePointerStartX = useRef<number>(0);
  const voicePointerStartY = useRef<number>(0);
  const voiceCapturedPointerId = useRef<number | null>(null);
  const [isVoiceLocked, setIsVoiceLocked] = useState(false);
  /** Live gesture preview while the user is holding the mic:
   *    'none'   – within thresholds; release = send.
   *    'cancel' – slid LEFT past VOICE_SLIDE_CANCEL_PX; release = discard.
   *    'lock'   – slid UP past VOICE_SLIDE_LOCK_PX; release = hands-free lock.
   *  Drives the trash/lock UI colors so the user can see which action will
   *  fire before they lift their finger. */
  const [voiceGestureHint, setVoiceGestureHint] = useState<'none' | 'cancel' | 'lock'>('none');

  // Reset the locked flag whenever the recorder returns to idle/error — keeps
  // the locked UI from "sticking" after the recording was sent or cancelled.
  useEffect(() => {
    if (voice.state === 'idle' || voice.state === 'error') {
      setIsVoiceLocked(false);
    }
  }, [voice.state]);

  // Clear the gesture hint as soon as recording stops (sent or cancelled) so
  // the next press starts from a clean neutral state.
  useEffect(() => {
    if (voice.state !== 'recording' && voice.state !== 'processing') {
      setVoiceGestureHint('none');
    }
  }, [voice.state]);

  const handleMicPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      // Refuse non-primary pointers — a second simultaneous touch (e.g. on the
      // trash button while the first finger holds the mic) must not race the
      // gesture decision; only the first finger drives the recorder.
      if (!e.isPrimary) return;
      if (voice.state !== 'idle' || isSending || isVoiceLocked) return;
      e.preventDefault();
      try {
        micButtonRef.current?.setPointerCapture(e.pointerId);
        voiceCapturedPointerId.current = e.pointerId;
      } catch {
        // Some webviews refuse capture; fall through — pointerup may fire on
        // the original element anyway, which is still correct in the common case.
      }
      voicePointerStartX.current = e.clientX;
      voicePointerStartY.current = e.clientY;
      voice.holdStart();
    },
    [voice, isSending, isVoiceLocked],
  );

  const releaseMicCapture = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    try {
      if (voiceCapturedPointerId.current === e.pointerId) {
        micButtonRef.current?.releasePointerCapture(e.pointerId);
        voiceCapturedPointerId.current = null;
      }
    } catch {
      // ignore
    }
  }, []);

  const handleMicPointerUp = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      releaseMicCapture(e);
      const slidLeftPx = voicePointerStartX.current - e.clientX;
      const slidUpPx = voicePointerStartY.current - e.clientY;

      // Vertical lock takes precedence over horizontal cancel — if the user
      // dragged a long way up and slightly left, they almost certainly meant
      // to lock, not cancel.
      if (slidUpPx >= VOICE_SLIDE_LOCK_PX) {
        // Recorder stays in 'recording' state; we just switch the UI.
        setIsVoiceLocked(true);
        return;
      }
      if (slidLeftPx >= VOICE_SLIDE_CANCEL_PX) {
        voice.holdCancel();
        return;
      }
      voice.holdEnd();
    },
    [voice, releaseMicCapture],
  );

  const handleMicPointerCancel = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      releaseMicCapture(e);
      // Reset start coords so a stale prior session can't contaminate the next
      // gesture if pointerdown ever lands without our handler resetting them.
      voicePointerStartX.current = 0;
      voicePointerStartY.current = 0;
      voice.holdCancel();
    },
    [voice, releaseMicCapture],
  );

  // Live gesture tracking while the mic is held. Pointer-capture (set on
  // pointerdown) keeps these events firing even if the finger slides off the
  // button onto the indicator strip or above the bar. We only update state
  // on threshold transitions so the render rate stays sane.
  const handleMicPointerMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!isRecordingVoice || isVoiceLocked) return;
      const slidLeftPx = voicePointerStartX.current - e.clientX;
      const slidUpPx = voicePointerStartY.current - e.clientY;
      let next: 'none' | 'cancel' | 'lock' = 'none';
      if (slidUpPx >= VOICE_SLIDE_LOCK_PX) {
        next = 'lock';
      } else if (slidLeftPx >= VOICE_SLIDE_CANCEL_PX) {
        next = 'cancel';
      }
      setVoiceGestureHint((prev) => (prev === next ? prev : next));
    },
    [isRecordingVoice, isVoiceLocked],
  );

  const handleCloseTicket = async (
    selectedServiceTypes: ServiceType[],
    selectedCategories: string[],
    closeNote?: string
  ) => {
    // Two paths through the same modal: single-close (legacy default) and
    // batch close (multi-select). Branch on selectionMode so the user sees
    // one consistent CategoryModal regardless of how many tickets they're
    // closing. After batch close we exit the conversation since the focused
    // ticket may have been one of those closed.
    if (selectionMode) {
      const ids = Array.from(selectedTicketIds);
      if (ids.length === 0) return;
      const result = await closeTicketsBatch(ids, selectedServiceTypes, selectedCategories, closeNote);
      if (result.ok) {
        setShowCategoryModal(false);
        setSelectionMode(false);
        setSelectedTicketIds(new Set());
        onBack();
      }
      return;
    }
    const success = await closeTicket(ticketId, selectedServiceTypes, selectedCategories, closeNote);
    if (success) {
      setShowCategoryModal(false);
      onBack();
    }
  };

  // Multi-select helpers. enterSelectionMode pre-selects whichever ticket
  // the user tapped (the one that triggered entry into selection mode),
  // not the currently-focused one — the gesture and the pre-selection
  // should agree about what the user just touched.
  const enterSelectionMode = useCallback((seedTicketId?: string) => {
    const seed = seedTicketId ?? ticket?.ticketId;
    if (!seed) return;
    setSelectedTicketIds(new Set([seed]));
    setSelectionMode(true);
  }, [ticket]);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedTicketIds(new Set());
  }, []);

  const toggleTicketSelection = useCallback((tid: string) => {
    setSelectedTicketIds((prev) => {
      const next = new Set(prev);
      if (next.has(tid)) next.delete(tid);
      else next.add(tid);
      return next;
    });
  }, []);

  // v4.1 — message multi-select helpers. The flow mirrors the ticket
  // multi-select above:
  //   - long-press a bubble  → setActiveDeleteModal({...})
  //   - tap Select in modal  → enter messageSelectionMode pre-seeded
  //   - tap Delete in modal  → directly commit the single-message delete
  //   - tap more bubbles     → toggleMessageSelection (only eligible rows
  //                            are selectable; ineligible rows have no
  //                            checkbox)
  //   - tap trash chip       → open the delete sheet with the batch
  //   - confirm in sheet     → call apiClient.deleteMessages; the
  //                            Socket.io 'message_deleted' WS event then
  //                            re-fetches the conversation tail.
  const handleMessageLongPress = useCallback(
    (msg: Message, anchorRect: DOMRect, bubbleHtml: string, isMine: boolean) => {
      if (messageSelectionMode) return; // already in select mode; do nothing
      setActiveDeleteModal({ message: msg, anchorRect, bubbleHtml, isMine });
    },
    [messageSelectionMode]
  );

  const enterMessageSelectionMode = useCallback((seed: Message) => {
    setSelectedMessageIds(new Set([seed.id]));
    setMessageSelectionMode(true);
  }, []);

  const exitMessageSelectionMode = useCallback(() => {
    setMessageSelectionMode(false);
    setSelectedMessageIds(new Set());
  }, []);

  const toggleMessageSelection = useCallback((id: number) => {
    setSelectedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // The Select path needs only the message; the Delete path is now handled
  // ENTIRELY inside MessageActionModal as an internal step transition (the
  // bubble clone + backdrop stay visible while the menu swaps to the two
  // destructive options). When the user picks an option there, we commit
  // via handleSingleDeleteConfirm below — the modal calls onClose itself.
  const handleModalSelect = useCallback(() => {
    const ctx = activeDeleteModal;
    if (!ctx) return;
    setActiveDeleteModal(null);
    enterMessageSelectionMode(ctx.message);
  }, [activeDeleteModal, enterMessageSelectionMode]);

  // Phase 33: Reply path out of the action modal. Mirrors
  // handleModalSelect's shape — pull the held message, close the modal,
  // surface the new state. The composer's quoted-preview bar (added in
  // plan 33-05) renders next from `replyTarget`; plan 33-06 threads the
  // target's DB id into the apiClient.sendReply call. Three modal paths
  // (Reply / Select / Delete) are independent — none cross-affects the
  // other two's state machines.
  // Phase 33: focus the composer + open the keyboard. iOS WKWebView
  // only opens the keyboard from a programmatic focus() if the call
  // happens synchronously inside the originating user gesture (touch
  // / click handler). Both Reply entry points (modal tap, swipe
  // release) invoke this from inside their gesture, so iOS allows it.
  // Also moves the caret to the END of any existing draft so the user
  // can keep typing seamlessly.
  const focusComposerForReply = useCallback(() => {
    const el = messageInputRef.current;
    if (!el) return;
    el.focus();
    // Place caret at the end of the contenteditable's text node.
    // Selection API works on contenteditable; we don't need execCommand.
    try {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false); // false = collapse to END
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    } catch {
      // Cross-browser quirks here are non-fatal; focus alone is enough
      // to bring up the keyboard. Caret-at-end is a polish nice-to-have.
    }
  }, []);

  const handleModalReply = useCallback(() => {
    const ctx = activeDeleteModal;
    if (!ctx) return;
    setActiveDeleteModal(null);
    setEditTarget(null);     // reply and edit are mutually exclusive
    setReplyTarget(ctx.message);
    focusComposerForReply();
  }, [activeDeleteModal, focusComposerForReply]);

  /**
   * Edit-message phase — open Edit mode pre-seeded with the held message.
   * Pre-fills the composer with the row's current text, shows the
   * EditContextBar above the input, and switches the send-button behavior
   * from POST /reply to PATCH /messages/:id (handled in the send path,
   * which checks editTarget before replyTarget).
   */
  const handleModalEdit = useCallback(() => {
    const ctx = activeDeleteModal;
    if (!ctx) return;
    setActiveDeleteModal(null);
    setReplyTarget(null);    // reply and edit are mutually exclusive
    setEditTarget(ctx.message);
    const newText = ctx.message.text ?? '';
    setMessage(newText);
    // Sync the contentEditable's innerText BEFORE the focus call so the
    // caret-at-end placement operates on the new text. Otherwise the
    // useLayoutEffect at line ~630 writes innerText AFTER focus and wipes
    // the selection, leaving the caret at position 0 of the just-loaded
    // text (visible bug: tapping Edit lands the cursor BEFORE the first
    // character instead of after the last one).
    const el = messageInputRef.current;
    if (el && el.innerText !== newText) el.innerText = newText;
    focusComposerForReply();
  }, [activeDeleteModal, focusComposerForReply]);

  /**
   * Phase 37 — insert a canned answer's body into the composer. INSERT-ONLY:
   * this fills the contentEditable for the agent to edit before sending via the
   * normal handleSendMessage path — it does NOT touch the reply/send pipeline.
   *
   * Appends to (never replaces) any existing draft with a newline separator so
   * a half-typed message isn't destroyed. The next value is computed once via a
   * functional setMessage update and the new text is applied imperatively to
   * the contentEditable innerText (mirroring handleModalEdit's sync) so the
   * caret-at-end placement in focusComposerForReply operates on the new text
   * rather than racing the line-633 useLayoutEffect. Computing inside the
   * updater also avoids a stale-closure read of `message`.
   */
  const insertCannedBody = useCallback((body: string) => {
    if (!body) return;
    setMessage((prev) => {
      const next = prev ? `${prev}\n${body}` : body;
      const el = messageInputRef.current;
      if (el && el.innerText !== next) el.innerText = next;
      return next;
    });
    focusComposerForReply();
  }, [focusComposerForReply]);

  // Phase 33: swipe-left-to-reply path. Identical net effect to
  // handleModalReply (seed `replyTarget`), but no modal context to close
  // — the swipe gesture never opens the modal. Caller-supplied message
  // is the row the user swiped on; MessageList already gates the gesture
  // on telegram_message_id != null so we don't re-validate here.
  const handleSwipeReply = useCallback((msg: Message) => {
    setActiveDeleteModal(null); // safety: close modal if it raced open
    setReplyTarget(msg);
    focusComposerForReply();
  }, [focusComposerForReply]);

  // Phase 33: tap an in-bubble quoted preview → scroll the original
  // message into view + give it a quick Messenger-style pulse
  // (briefly larger + heavier shadow, springs back). No coloured ring
  // — the quoted preview itself already carries an iOS-blue accent
  // stripe; layering another blue ring on the target reads as "two
  // blue lines" instead of "this is the message you tapped".
  //
  // Timing (matches Messenger feel):
  //   0ms   → set fast cubic-bezier transition, scale 1.06 + soft shadow
  //   180ms → swap to a longer ease-out transition for the spring-back
  //   200ms → scale back to 1 + drop the shadow
  //   500ms → clear the inline style overrides so the bubble returns to
  //           pristine state (so subsequent renders don't keep stale
  //           transition strings)
  const handleJumpToMessage = useCallback((targetMessageId: number) => {
    const root = scrollAreaRef.current;
    if (!root) return;
    const el = root.querySelector<HTMLElement>(
      `[data-message-id="${targetMessageId}"]`
    );
    if (!el) {
      // Target may be paginated above the loaded window. Best-effort:
      // give up silently rather than fetching, since the chat history
      // already keeps the technician's reply-target in load range
      // most of the time. A future plan can add pagination-aware jump.
      return;
    }
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const bubbleEl = el.querySelector<HTMLElement>('[data-message-bubble]') ?? el;
    const prevTransition = bubbleEl.style.transition;
    const prevTransform = bubbleEl.style.transform;
    const prevTransformOrigin = bubbleEl.style.transformOrigin;
    const prevBoxShadow = bubbleEl.style.boxShadow;
    // Scale from the bubble's natural origin so the pulse looks centered
    // regardless of which side of the chat the bubble is on. willChange
    // is a one-frame hint to the compositor for a smoother transform.
    bubbleEl.style.transformOrigin = 'center';
    bubbleEl.style.transition = 'transform 180ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 180ms ease-out';
    bubbleEl.style.transform = 'scale(1.06)';
    bubbleEl.style.boxShadow = '0 6px 20px rgba(0,0,0,0.18)';
    window.setTimeout(() => {
      bubbleEl.style.transition = 'transform 280ms ease-out, box-shadow 280ms ease-out';
      bubbleEl.style.transform = 'scale(1)';
      bubbleEl.style.boxShadow = prevBoxShadow;
    }, 200);
    window.setTimeout(() => {
      bubbleEl.style.transition = prevTransition;
      bubbleEl.style.transform = prevTransform;
      bubbleEl.style.transformOrigin = prevTransformOrigin;
    }, 520);
  }, []);

  // Phase 33: when ReplyContextBar / attachment / error strips mount,
  // they shrink the message scroll area by ~50 px. Without touching
  // scrollTop, the last visible messages slip BEHIND the new strip.
  // useLayoutEffect runs synchronously after the DOM update so we can
  // read the post-mount clientHeight and bump scrollTop by the same
  // delta the viewport just shrank by — keeping visual content in the
  // same place regardless of where the user was scrolled. Also
  // re-fires the standard tail-follow if the user was already pinned
  // to the bottom (Telegram's behavior).
  const prevScrollClientHeightRef = useRef<number>(0);
  useLayoutEffect(() => {
    const root = scrollAreaRef.current;
    if (!root) return;
    const prevHeight = prevScrollClientHeightRef.current;
    const nextHeight = root.clientHeight;
    prevScrollClientHeightRef.current = nextHeight;
    if (prevHeight === 0) return; // first run — nothing to compare
    const delta = prevHeight - nextHeight;
    if (delta === 0) return;
    if (nearBottomRef.current) {
      // Already pinned to the bottom — let the existing tail-follow path
      // take it the rest of the way. requestAnimationFrame so the bar's
      // layout has fully settled before we measure scrollHeight.
      requestAnimationFrame(() => {
        const el = scrollAreaRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
      });
      return;
    }
    // User was scrolled up — keep their visual anchor put by absorbing
    // the viewport shrink into scrollTop (positive delta = bar appeared;
    // negative = bar dismissed).
    root.scrollTop += delta;
  }, [replyTarget?.id, attachments.length, attachmentError, voice.errorMsg]);

  // Phase 33: when the technician enters Reply mode, the iOS keyboard
  // animates up (~250 ms) and further shrinks the visible viewport.
  // The replied-to message itself frequently ends up hidden under
  // either the ReplyContextBar or the keyboard. UX rule the user
  // asked for: "if not slip just keep that place, but if slip just
  // push it to top to not slip" — scroll the target into view ONLY
  // when it isn't currently visible; leave the scroll alone when it
  // already is.
  //
  // Timing: 350 ms covers the iOS keyboard animation across iOS 14+
  // (250-300 ms typical) plus a small safety margin. visualViewport
  // resize would be more precise but isn't reliably wired in every
  // WKWebView shell — a fixed delay is robust and the worst case is
  // a 50 ms "snap" if the keyboard finished early.
  useEffect(() => {
    if (!replyTarget) return;
    const root = scrollAreaRef.current;
    if (!root) return;
    const timer = setTimeout(() => {
      const el = scrollAreaRef.current;
      if (!el) return;
      const targetEl = el.querySelector<HTMLElement>(
        `[data-message-id="${replyTarget.id}"]`
      );
      if (!targetEl) return;
      const rootRect = el.getBoundingClientRect();
      const targetRect = targetEl.getBoundingClientRect();
      const hiddenBelow = targetRect.bottom > rootRect.bottom;
      const hiddenAbove = targetRect.top < rootRect.top;
      if (!hiddenBelow && !hiddenAbove) return; // already in view, do nothing
      // Land target near the bottom of the visible area (just above the
      // reply bar), matching Telegram's behavior. `block: 'end'` aligns
      // the target's bottom with the scroll container's bottom — which
      // sits flush with the ReplyContextBar above the keyboard.
      targetEl.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }, 350);
    return () => clearTimeout(timer);
  }, [replyTarget?.id]);

  const openDeleteSheetForBatch = useCallback(() => {
    if (selectedMessageIds.size === 0) return;
    const batch = conversationMessages.filter((m) => selectedMessageIds.has(m.id));
    setActiveDeleteSheet(batch);
  }, [selectedMessageIds, conversationMessages]);

  // Shared delete primitive. Strict policy — server rejects the batch with
  // 400 if any row can't also be unsent on Telegram. The UI prevents this
  // by greying out ineligible rows in selection mode and disabling the
  // Delete button in the single-message modal, but we still surface the
  // server error if it slips through.
  const runDelete = useCallback(
    async (msgs: Message[]) => {
      if (!ticket || msgs.length === 0) return;
      const ids = msgs.map((m) => m.id);
      const res = await apiClient.deleteMessages(ticket.ticketId, ids);
      if (!res.success) {
        console.error('Delete failed:', res.error);
        alert(`Delete failed: ${res.error ?? 'Unknown error'}`);
        return;
      }
      // WS 'message_deleted' event syncs the local list; no optimistic mutation here.
    },
    [ticket],
  );

  const performDelete = useCallback(
    async () => {
      const batch = activeDeleteSheet ?? [];
      setActiveDeleteSheet(null);
      await runDelete(batch);
      exitMessageSelectionMode();
    },
    [activeDeleteSheet, runDelete, exitMessageSelectionMode],
  );

  const handleSingleDeleteConfirm = useCallback(
    async () => {
      const msg = activeDeleteModal?.message;
      setActiveDeleteModal(null);
      if (msg) await runDelete([msg]);
    },
    [activeDeleteModal, runDelete],
  );

  // Eagerly load the user's active tickets when the ticket detail mounts —
  // we need this map upfront so default-mode ticket dividers can show their
  // "tap to select" affordance immediately (rather than waiting for the
  // user to discover a Select button). userId is the Telegram chat id;
  // safe in JS Number range for all Telegram IDs.
  useEffect(() => {
    if (!ticket?.userId) return;
    void loadUserActiveTickets(Number(ticket.userId));
  }, [ticket?.userId, loadUserActiveTickets]);

  // Note: an earlier draft of this PR added a second effect here that
  // scanned `conversationMessages` for unknown ticket ids and re-fetched
  // active tickets as a safety net. Removed because it had an infinite-
  // fetch loop for users with closed historical tickets in their
  // conversation:
  //   - The `loadUserActiveTickets` hook always sets a fresh array
  //     reference (apiClient returns a fresh JSON parse each call), so
  //     React doesn't bail out of the re-render even when content is
  //     identical.
  //   - `activeTicketIdSet` (useMemo on userActiveTickets) then gets a
  //     new Set reference too.
  //   - The effect's `activeTicketIdSet` dep changes, effect re-fires,
  //     the historical ticket is still unknown, fetch fires again, loop
  //     at ~5–10 req/sec.
  //
  // The `new_ticket` WebSocket handler above is the right shape — it
  // fires exactly when a new ticket is created, not on every render.
  // For the rare missed-event case (WS disconnected at the exact moment
  // of creation), the existing 30s conversation poll picks it up.

  if (isLoadingTicket && !ticket) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-50 z-50">
        <div className="text-center">
        <div className="w-8 h-8 border-2 border-text-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <div className="text-gray-500">Loading ticket...</div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-gray-50 z-50">
        <div className="text-4xl mb-4">❌</div>
        <div className="text-gray-500 mb-4">Ticket not found</div>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg"
        >
          Go Back
        </button>
      </div>
    );
  }

  const displayName = ticket.firstName || ticket.username || 'User';

  /** Nav title: full name only (no @username) */
  const headerFullName =
    [ticket.firstName, ticket.lastName].filter(Boolean).join(' ').trim() ||
    ticket.firstName ||
    'User';

  /** Subtitle: Telegram @username when set, else numeric id; then status */
  const telegramIdentity = ticket.username
    ? `@${ticket.username}`
    : String(ticket.userId);

  const formatPhoneForHeader = (raw?: string | null): string | null => {
    if (!raw) return null;
    const digits = String(raw).replace(/[^\d]/g, '');
    if (digits.length < 4) return digits || null;
    const country = digits.slice(0, 3);
    const rest = digits.slice(3);
    // Cambodia-style grouping: (+855) 96 7777 515
    if (country === '855') {
      if (rest.length === 9) return `(+855) ${rest.slice(0, 2)} ${rest.slice(2, 6)} ${rest.slice(6)}`;
      if (rest.length === 8) return `(+855) ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5)}`;
    }
    return `(+${country}) ${rest}`;
  };

  const headerSubtitleParts = [
    formatPhoneForHeader(ticket.phoneNumber),
    telegramIdentity,
  ].filter(Boolean) as string[];

  const STATUS_ICON: Record<string, { Icon: LucideIcon; color: string; label: string }> = {
    open:        { Icon: Circle,       color: '#34C759', label: 'Open' },
    in_progress: { Icon: CircleDot,    color: '#007AFF', label: 'In Progress' },
    escalated:   { Icon: AlertTriangle,color: '#FF9500', label: 'Escalated' },
    closed:      { Icon: CheckCircle2, color: '#8E8E93', label: 'Closed' },
  };
  const statusIconInfo = STATUS_ICON[ticket.status] ?? STATUS_ICON.open;

  // Show send button only when there is content to send
  const canSend = message.trim().length > 0 || attachments.length > 0;

  return (
    <VoiceSpeedProvider resetKey={ticketId}>
    <div className="fixed inset-0 flex flex-col z-50" style={{ background: '#A8C99B' }}>

      {/* ── iOS-style single-row frosted-glass nav bar ── */}
      <div
        className="flex-none z-10 bg-white/95 backdrop-blur-md safe-area-top"
        style={{ borderBottom: '0.5px solid rgba(60,60,67,0.36)' }}
      >
        <div className="flex items-center h-[44px] px-2 gap-1 max-w-4xl mx-auto w-full">

          {messageSelectionMode ? (
            // v4.1 message multi-select header — iMessage-style layout:
            // Cancel text button on the LEFT, "N Selected" pill centred,
            // right slot empty. Cancel matches the default-mode Back
            // button's typography for a familiar iOS feel.
            <>
              <div className="min-w-[64px] flex">
                <button
                  type="button"
                  onClick={exitMessageSelectionMode}
                  className="flex items-center text-[#007AFF] px-1 touch-active"
                  aria-label="Cancel selection"
                >
                  <span className="text-[17px] leading-none">Cancel</span>
                </button>
              </div>
              <div className="flex-1 flex justify-center min-w-0 px-1">
                <span
                  className="px-4 py-1 rounded-full text-[15px] font-semibold text-black/85 select-none"
                  style={{
                    background: 'rgba(120,120,128,0.16)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  {selectedMessageIds.size} Selected
                </span>
              </div>
              <div className="min-w-[64px]" aria-hidden />
            </>
          ) : selectionMode ? (
            <button
              type="button"
              onClick={exitSelectionMode}
              className="flex items-center gap-0.5 text-[#007AFF] px-1 min-w-[64px] touch-active"
              aria-label="Cancel selection"
            >
              <span className="text-[17px] leading-none">Cancel</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-0.5 text-[#007AFF] px-1 min-w-[64px] touch-active"
              aria-label="Back"
            >
              <ChevronLeft className="h-6 w-6 flex-shrink-0" strokeWidth={2.5} aria-hidden />
              <span className="text-[17px] leading-none">Back</span>
            </button>
          )}

          {/* Centre + Right slots — suppressed during message-selection mode
              (the messageSelectionMode branch above already renders all
              three columns as a single fragment). */}
          {!messageSelectionMode && (
            <>
              {/* Centre — user info in default mode, "N selected" while selecting */}
              {selectionMode ? (
                <div className="flex-1 flex flex-col items-center min-w-0 px-1">
                  <span className="text-[17px] font-semibold text-black leading-tight">
                    {selectedTicketIds.size} selected
                  </span>
                  <span className="text-[11px] leading-tight" style={{ color: '#6C6C70' }}>
                    Tap a ticket to add or remove
                  </span>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center min-w-0 px-1">
                  <div className="flex items-center gap-1.5 min-w-0 max-w-full">
                    <span className="text-[17px] font-semibold text-black leading-tight truncate max-w-full">
                      {headerFullName}
                    </span>
                    <span
                      className="flex-shrink-0"
                      title={statusIconInfo.label}
                      aria-label={statusIconInfo.label}
                    >
                      <statusIconInfo.Icon
                        className="h-4 w-4"
                        style={{ color: statusIconInfo.color }}
                        aria-hidden
                      />
                    </span>
                  </div>
                  <span className="text-[11px] leading-tight truncate max-w-full" style={{ color: '#6C6C70' }}>
                    {headerSubtitleParts.join(' · ')}
                  </span>
                </div>
              )}

              {/* Right — "Close N" in selection mode, plain "Close" otherwise.
                  Selection mode is entered by TAPPING a ticket divider in the
                  conversation (gesture-discoverable on mobile) instead of a
                  dedicated Select button — keeps the header at its original
                  spacious layout for the common single-ticket case. */}
              <div className="min-w-[64px] flex justify-end">
                {selectionMode ? (
                  <button
                    type="button"
                    onClick={() => setShowCategoryModal(true)}
                    disabled={isSending || selectedTicketIds.size === 0}
                    className="flex items-center gap-1 px-1 touch-active disabled:opacity-40"
                    aria-label={`Close ${selectedTicketIds.size} selected tickets`}
                  >
                    <X className="h-4 w-4 text-[#FF3B30]" strokeWidth={2} aria-hidden />
                    <span className="text-[17px] text-[#FF3B30]">Close {selectedTicketIds.size}</span>
                  </button>
                ) : (
                  ticket.status !== 'closed' && (
                    <button
                      type="button"
                      onClick={() => setShowCategoryModal(true)}
                      disabled={isSending}
                      className="flex items-center gap-1 px-1 touch-active disabled:opacity-50"
                    >
                      <X className="h-4 w-4 text-[#FF3B30]" strokeWidth={2} aria-hidden />
                      <span className="text-[17px] text-[#FF3B30]">Close</span>
                    </button>
                  )
                )}
              </div>
            </>
          )}

        </div>
      </div>

      {/* Phase 32 inline-divider: SourceBanner replaced by per-divider inline render in
          MessageList. The SourceBanner component file is kept intact for future reuse
          (e.g. a different surface or admin panel). Commenting out the JSX block here
          rather than deleting, so it's easy to restore if needed. */}
      {/* {ticket.externalSource ? (
        <div className="flex-none border-b bg-white">
          <div className="max-w-4xl mx-auto">
            <SourceBanner
              source={ticket.externalSource}
              referenceId={ticket.externalReferenceId}
              title={ticket.externalTitle}
              descriptionMd={ticket.externalDescriptionMd}
              onRefresh={handleRefreshTicketExternal}
              isRefreshing={refreshingTicketIds.has(ticketId)}
              refreshFailed={failedRefreshTicketIds.has(ticketId)}
            />
          </div>
        </div>
      ) : null} */}

      {/* ── Message area – Telegram-style gradient background ── */}
      <div
        ref={scrollAreaRef}
        onScroll={handleMessagesScroll}
        className="flex-1 overflow-y-auto"
        style={{
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'auto',
          // background: 'linear-gradient(167.22deg, rgba(201,209,133,0.85) 2%, rgba(144,187,136,0.85) 45%, rgba(201,209,133,0.85) 90%)',
          background: '#ffff',
        }}
      >
        <div className="max-w-4xl mx-auto">
          <div ref={topSentinelRef} className="h-1 w-full" aria-hidden />
          {/* Intentionally no loaders here — messages appear silently when ready. */}
          {conversationMessages.length > 0 ? (
            <MessageList
              messages={conversationMessages}
              userFirstName={displayName}
              ticketsExternal={ticketsExternal}
              onRefreshTicketExternal={handleRefreshTicketExternal}
              refreshingTicketIds={refreshingTicketIds}
              failedRefreshTicketIds={failedRefreshTicketIds}
              selectionMode={selectionMode}
              selectedTicketIds={selectedTicketIds}
              selectableTicketIds={activeTicketIdSet}
              onToggleTicketSelection={toggleTicketSelection}
              onTicketDividerTap={(tid) => enterSelectionMode(tid)}
              onTicketDividerLongPressForReopen={isLevel2 ? (tid) => {
                setReopenTargetTicketId(tid);
                setReopenReason('');
                setReopenError(null);
                setShowReopenModal(true);
              } : undefined}
              onMessageLongPress={handleMessageLongPress}
              onMessageSwipeReply={handleSwipeReply}
              onJumpToMessage={handleJumpToMessage}
              messageSelectionMode={messageSelectionMode}
              selectedMessageIds={selectedMessageIds}
              onToggleMessageSelection={toggleMessageSelection}
              onExpandClosedGroup={() => setHasOptedIntoClosedHistory(true)}
              compactClosedTickets={!hasOptedIntoClosedHistory}
            />
          ) : conversationLoadingInitial ? null : (
            <MessageList
              messages={conversationMessages}
              userFirstName={displayName}
              ticketsExternal={ticketsExternal}
              onRefreshTicketExternal={handleRefreshTicketExternal}
              refreshingTicketIds={refreshingTicketIds}
              failedRefreshTicketIds={failedRefreshTicketIds}
              selectionMode={selectionMode}
              selectedTicketIds={selectedTicketIds}
              selectableTicketIds={activeTicketIdSet}
              onToggleTicketSelection={toggleTicketSelection}
              onTicketDividerTap={(tid) => enterSelectionMode(tid)}
              onTicketDividerLongPressForReopen={isLevel2 ? (tid) => {
                setReopenTargetTicketId(tid);
                setReopenReason('');
                setReopenError(null);
                setShowReopenModal(true);
              } : undefined}
              onMessageLongPress={handleMessageLongPress}
              onMessageSwipeReply={handleSwipeReply}
              onJumpToMessage={handleJumpToMessage}
              messageSelectionMode={messageSelectionMode}
              selectedMessageIds={selectedMessageIds}
              onToggleMessageSelection={toggleMessageSelection}
              onExpandClosedGroup={() => setHasOptedIntoClosedHistory(true)}
              compactClosedTickets={!hasOptedIntoClosedHistory}
            />
          )}
          {/* Shadow quick-reply suggestions — rendered at the TAIL of the
              conversation (inside the scroll area) so they scroll WITH the
              message stream rather than sitting in a pinned bar. They read as
              ghost "next reply" bubbles on the agent's side. The component
              returns null when there's nothing to show; hidden in selection
              modes / closed tickets, with the rest gated by `visible`. */}
          {ticket.status !== 'closed' && !messageSelectionMode && !selectionMode && (
            <ShadowSuggestions
              visible={
                message.trim() === '' &&
                attachments.length === 0 &&
                !replyTarget &&
                !editTarget &&
                !isRecordingVoice &&
                // Option 1 — show only for a still-NEW ticket (no agent reply
                // yet). status flips 'open' → 'in_progress' on the first reply.
                ticket.status === 'open'
              }
              suggestedCaseType={
                ticket.externalCaseType === 'SV' || ticket.externalCaseType === 'WL'
                  ? ticket.externalCaseType
                  : null
              }
              onInsert={insertCannedBody}
            />
          )}
          <div ref={messagesEndRef} className="h-2" />
        </div>
      </div>

      {/* Attachment Preview */}
      {attachments.length > 0 && (
        <div className="flex-none bg-white border-t px-4 py-2">
          <div className="flex gap-2 overflow-x-auto">
            {attachments.map((att, idx) => (
              <div key={idx} className="relative flex-shrink-0">
                {att.type === 'image' ? (
                  <img
                    src={`data:image/jpeg;base64,${att.base64}`}
                    alt="attachment"
                    className="w-16 h-16 object-cover rounded"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                    <span className="text-2xl">📄</span>
                  </div>
                )}
                <button
                  onClick={() => handleRemoveAttachment(idx)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* File size error banner */}
      {attachmentError && (
        <div className="flex-none bg-red-50 border-t border-red-200 px-4 py-2 flex items-center justify-between gap-2">
          <span className="text-sm text-red-600">{attachmentError}</span>
          <button onClick={() => setAttachmentError(null)} className="text-red-400 hover:text-red-600 text-lg leading-none">×</button>
        </div>
      )}

      {/* Phase 33: Reply target preview — shown when the technician has
       *  tapped Reply on a held message (handleModalReply sets replyTarget).
       *  Tapping × clears state only — no API call. The actual API send
       *  happens through handleSendMessage, which snapshots and clears
       *  replyTarget before the await so the bar disappears immediately
       *  regardless of send success/failure. */}
      {replyTarget && (
        <ReplyContextBar
          target={replyTarget}
          onCancel={() => setReplyTarget(null)}
        />
      )}

      {/* Edit-message phase — shown when the technician has tapped Edit
       *  on a held message. setMessage was called with the current text
       *  in handleModalEdit, so the composer is pre-filled. The send
       *  path checks `editTarget` before `replyTarget` and dispatches
       *  PATCH instead of POST. Tapping × restores normal compose mode
       *  WITHOUT clearing the input — the technician may have already
       *  started typing changes they want to keep for a fresh reply. */}
      {editTarget && (
        <EditContextBar
          target={editTarget}
          onCancel={() => setEditTarget(null)}
        />
      )}

      {/* Voice recorder error banner — shown when the mic flow hit a hard stop
       *  (permission off, recorder failure). Tap × to dismiss and try again. */}
      {voice.errorMsg && (
        <div className="flex-none bg-red-50 border-t border-red-200 px-4 py-2 flex items-center justify-between gap-2">
          <span className="text-sm text-red-600">{voice.errorMsg}</span>
          <button
            onClick={voice.dismissError}
            aria-label="Dismiss voice error"
            className="text-red-400 hover:text-red-600 text-lg leading-none"
          >
            ×
          </button>
        </div>
      )}

      {/* ── iOS-style frosted-glass input bar ──
          Hidden during message-selection mode — the Delete (N) action bar
          takes this slot instead, and typing while selecting messages is
          nonsensical (Telegram convention). */}
      {ticket.status !== 'closed' && !messageSelectionMode && (
        <div
          className="flex-none z-10 bg-white/80 backdrop-blur-md safe-area-bottom"
          style={{ borderTop: '0.5px solid rgba(60,60,67,0.36)' }}
        >
          {/* Reply-target picker. The composer replies to the OPENED ticket
              (props.ticketId); when the user has >1 active ticket in this
              unified conversation, that binding isn't obvious AND the agent may
              want to answer a different one. This control both LABELS the
              current target (ticket + case type) and, on tap, opens a menu of
              the user's open tickets — selecting one re-opens it (onSelectTicket)
              so the composer + quick-reply suggestions follow. Hidden in the
              common single-ticket case to avoid clutter. */}
          {userActiveTickets.length > 1 && (
            <div className="max-w-4xl mx-auto px-3 pt-2 relative">
              <button
                type="button"
                onClick={() => setShowReplyTargetMenu((o) => !o)}
                aria-expanded={showReplyTargetMenu}
                className="flex items-center gap-1.5 text-[11px] text-gray-500 touch-active"
              >
                <CornerUpLeft className="h-3 w-3 shrink-0" aria-hidden />
                <span>Replying to</span>
                <span className="font-semibold text-gray-700">{ticket.ticketId}</span>
                {ticket.externalCaseType === 'SV' || ticket.externalCaseType === 'WL' ? (
                  <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide">
                    {ticket.externalCaseType}
                  </span>
                ) : null}
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${showReplyTargetMenu ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>

              {showReplyTargetMenu && (
                <>
                  {/* Tap-away backdrop (transparent) so the menu closes on an
                      outside tap without a global listener. */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowReplyTargetMenu(false)}
                    aria-hidden
                  />
                  <div className="absolute z-20 bottom-full left-3 mb-1 min-w-[200px] rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
                    {userActiveTickets.map((t) => {
                      const isCurrent = t.ticketId === ticket.ticketId;
                      const ct =
                        t.externalCaseType === 'SV' || t.externalCaseType === 'WL'
                          ? t.externalCaseType
                          : null;
                      return (
                        <button
                          key={t.ticketId}
                          type="button"
                          onClick={() => {
                            setShowReplyTargetMenu(false);
                            if (!isCurrent && onSelectTicket) onSelectTicket(t);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-left hover:bg-gray-50 active:bg-gray-100"
                        >
                          <span className={isCurrent ? 'font-semibold text-[#007AFF]' : 'text-gray-800'}>
                            {t.ticketId}
                          </span>
                          {ct ? (
                            <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide">
                              {ct}
                            </span>
                          ) : null}
                          {isCurrent ? (
                            <Check className="ml-auto h-4 w-4 text-[#007AFF]" aria-hidden />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex items-end gap-2 px-3 py-2 max-w-4xl mx-auto">

            {/* Phase 37 — "⚡" canned-responses trigger. Ungated (available to
             *  ALL technicians — only the admin CRUD screen is level-gated);
             *  hidden while recording so it can't be tapped mid-gesture. Styled
             *  like the Paperclip attach button (same w-9 h-9 box / muted
             *  color) so it aligns to the mic layout reference. Inserts the
             *  selected answer's body into the composer — does NOT send.
             *  Hidden once the composer has text: it reclaims width for a
             *  growing multi-line draft, and inserting a canned snippet
             *  mid-typing is rare (the shadow strip covers the empty-draft
             *  case). The 📎 attach button stays — captioned attachments are a
             *  real flow. */}
            {!isRecordingVoice && message.trim() === '' && (
              <button
                type="button"
                onClick={() => setShowCannedPicker(true)}
                aria-label="Canned responses"
                className="flex-shrink-0 w-9 h-9 flex items-center justify-center touch-active"
                style={{ color: '#858E99' }}
              >
                <Zap className="w-[22px] h-[22px]" strokeWidth={2} aria-hidden />
              </button>
            )}

            {/* Attachment – SuperApp (hidden while voice recording so it can't
             *  be tapped). Wrapped in a w-9 h-9 box so the icon sits at the
             *  same vertical center as the idle mic button — the mic is the
             *  layout reference, everything else aligns to it. */}
            {isInSuperApp && !isRecordingVoice && (
              <button
                type="button"
                onClick={() => setShowMediaPicker(true)}
                aria-label="Add attachment"
                className="flex-shrink-0 w-9 h-9 flex items-center justify-center touch-active"
                style={{ color: '#858E99' }}
              >
                <Paperclip className="w-[22px] h-[22px]" strokeWidth={2} aria-hidden />
              </button>
            )}

            {/* Trash – takes the Paperclip slot while recording. Tap to cancel
             *  the recording (alternative to slide-to-cancel). Same w-9 h-9
             *  box as the paperclip so it aligns identically.
             *  Color flips to iOS blue (#007AFF) when the user's finger has
             *  slid past the cancel threshold — confirms that releasing now
             *  will discard the recording.
             *
             *  isVoiceLocked is reset synchronously here (not waiting for the
             *  state-machine echo via the useEffect at the top of the file).
             *  voice.holdCancel() → finalize('cancel') goes recording → processing
             *  → idle asynchronously; if the bridge's stopRecording stalls or
             *  errors, the useEffect never fires and the blue locked-send
             *  button gets stuck in the layout. Resetting on the user gesture
             *  makes the UI reflect intent immediately and decouples it from
             *  any async failure on the native side. */}
            {isRecordingVoice && (
              <button
                type="button"
                onClick={() => {
                  setIsVoiceLocked(false);
                  voice.holdCancel();
                }}
                aria-label="Cancel recording"
                className="flex-shrink-0 w-9 h-9 flex items-center justify-center touch-active transition-colors duration-100"
                style={{ color: voiceGestureHint === 'cancel' ? '#007AFF' : '#FF3B30' }}
              >
                <Trash2
                  className={`w-[22px] h-[22px] ${voiceGestureHint === 'cancel' ? 'scale-110' : ''} transition-transform duration-100`}
                  strokeWidth={2}
                  aria-hidden
                />
              </button>
            )}

            {/* Attachment – Web file input (suppressed while voice recording) */}
            {!isInSuperApp && !isRecordingVoice && (
              <>
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const typeError = checkFileAllowed(file.name);
                      if (typeError) {
                        setAttachmentError(typeError);
                        e.target.value = '';
                        return;
                      }
                      if (file.size > MAX_FILE_SIZE_BYTES) {
                        setAttachmentError(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
                        e.target.value = '';
                        return;
                      }
                      setAttachmentError(null);
                      const reader = new FileReader();
                      reader.onload = () => {
                        const base64 = (reader.result as string).split(',')[1];
                        setAttachments(prev => [
                          ...prev,
                          {
                            type: file.type.startsWith('image/') ? 'image' : 'document',
                            base64,
                            fileName: file.name,
                          },
                        ]);
                      };
                      reader.readAsDataURL(file);
                      e.target.value = '';
                    }
                  }}
                />
                <label
                  htmlFor="file-upload"
                  aria-label="Add attachment"
                  className="flex-shrink-0 w-9 h-9 flex items-center justify-center cursor-pointer touch-active"
                  style={{ color: '#858E99' }}
                >
                  <Paperclip className="w-[22px] h-[22px]" strokeWidth={2} aria-hidden />
                </label>
              </>
            )}

            {/* Pill input — hidden while recording so the bar shows the timer
             *  + slide-to-cancel hint instead.
             *
             *  Enter intentionally produces a newline (textarea's default).
             *  Pressing Enter to send is the Slack/Discord desktop pattern;
             *  this is a mobile-first chat surface (iPhone via SuperApp),
             *  where the platform convention — and what users coming from
             *  Telegram / Messages / WhatsApp expect — is that the keyboard
             *  return key inserts a newline and sending requires the
             *  dedicated Send button. */}
            {!isRecordingVoice && (
              <div className="flex-1 relative flex">
                {/* Placeholder — rendered as an aria-hidden sibling instead of
                 *  contentEditable's :empty::before trick, because pressing
                 *  Enter in a contentEditable leaves a stray <br> behind and
                 *  :empty stops matching even though the user sees nothing. */}
                {message === '' && (
                  <span
                    aria-hidden
                    className="absolute pointer-events-none select-none text-[17px]"
                    style={{
                      top: 5,
                      left: 15,
                      color: '#86888c',
                      lineHeight: '22px',
                    }}
                  >
                    Message
                  </span>
                )}
                <div
                  ref={messageInputRef}
                  contentEditable={!isSending}
                  role="textbox"
                  aria-multiline="true"
                  aria-label="Message"
                  suppressContentEditableWarning
                  onInput={(e) => {
                    setMessage((e.currentTarget as HTMLDivElement).innerText);
                  }}
                  onCompositionStart={() => {
                    isComposingRef.current = true;
                  }}
                  onCompositionEnd={() => {
                    isComposingRef.current = false;
                    syncMessageInputHeight();
                  }}
                  onPaste={(e) => {
                    // Force paste-as-plain-text. Default contenteditable paste
                    // pulls in HTML/styling from the source app (Notes,
                    // Safari, etc.), which we don't want in a chat composer.
                    e.preventDefault();
                    const text = e.clipboardData.getData('text/plain');
                    document.execCommand('insertText', false, text);
                  }}
                  className="w-full focus:outline-none text-[17px] leading-snug bg-white overflow-y-auto"
                  style={{
                    border: '1px solid #D1D1D6',
                    borderRadius: '16.5px',
                    padding: '5px 14px',
                    color: '#000',
                    minHeight: '34px',
                    maxHeight: `${MESSAGE_INPUT_MAX_PX}px`,
                    lineHeight: '22px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                />
              </div>
            )}

            {/* Recording indicator — slots into the textarea position while
             *  the mic is held. Pulsing dot + timer + contextual hint
             *  ("← Slide to cancel" while held; "Recording…" while locked,
             *  since slide-to-cancel no longer applies once the finger is up).
             *  All CSS user-select / callout / tap-highlight properties are
             *  disabled here because the user's finger inevitably slides over
             *  this strip while holding the mic, and iOS would otherwise
             *  start a text selection on the hint label (the magnifier + blue
             *  selection handles seen in testing). */}
            {isRecordingVoice && (
              <div
                className="flex-1 flex items-center gap-2 px-3 h-[34px] rounded-[16.5px] bg-white"
                style={{
                  border: '1px solid #D1D1D6',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'none',
                  pointerEvents: 'none',
                }}
              >
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" aria-hidden />
                <span className="font-mono text-[15px] text-text-primary tabular-nums">
                  {voice.state === 'processing' ? '...' : formatVoiceDuration(voice.duration)}
                </span>
                <span className="text-[12px] text-text-tertiary ml-auto truncate">
                  {isVoiceLocked ? 'Recording — tap send when done' : '← Slide to cancel'}
                </span>
              </div>
            )}

            {/* Send – appears when there's text/attachments AND we're not recording */}
            {canSend && !isRecordingVoice && (
              <button
                type="button"
                onClick={handleSendMessage}
                disabled={isSending}
                aria-label="Send"
                className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center touch-active disabled:opacity-50 transition"
                style={{ background: '#007AFF' }}
              >
                {isSending ? (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <SendHorizontal className="w-[18px] h-[18px] text-white" strokeWidth={2} aria-hidden />
                )}
              </button>
            )}

            {/* Right-side button: state machine has three modes.
             *  - Idle / unlocked-recording → press-and-hold mic. While held,
             *    the icon scales into a filled red circle; on release we
             *    decide between send/cancel/lock based on the slide direction.
             *  - Locked-recording → tap-to-send button (regular click). User
             *    has already released the mic in this mode.
             *  - Hidden when there's text to send (Send icon for text takes over). */}
            {(!canSend || isRecordingVoice) && isInSuperApp && !isVoiceLocked && (
              <div className="relative flex-shrink-0">
                {/* Floating "slide up to lock" hint — only shown while finger
                 *  is actively holding the mic and recording hasn't been
                 *  locked yet. pointer-events: none so the user's finger
                 *  passes through it onto the captured mic button.
                 *  Pill fills with iOS blue + scales up the moment the user
                 *  crosses the lock threshold, confirming the gesture is
                 *  recognized before they release. */}
                {isRecordingVoice && (
                  <div
                    className={`absolute left-1/2 -translate-x-1/2 -top-16 flex flex-col items-center gap-1 transition-transform duration-100 ${
                      voiceGestureHint === 'lock' ? 'scale-110' : ''
                    }`}
                    style={{
                      pointerEvents: 'none',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      WebkitTouchCallout: 'none',
                    }}
                    aria-hidden
                  >
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center shadow-sm transition-colors duration-100"
                      style={{
                        background: voiceGestureHint === 'lock' ? '#007AFF' : '#FFFFFF',
                        border: `1px solid ${voiceGestureHint === 'lock' ? '#007AFF' : '#D1D1D6'}`,
                      }}
                    >
                      <Lock
                        className="w-4 h-4 transition-colors duration-100"
                        style={{ color: voiceGestureHint === 'lock' ? '#FFFFFF' : '#858E99' }}
                        strokeWidth={2}
                      />
                    </div>
                    <span
                      className="text-[10px] whitespace-nowrap transition-colors duration-100"
                      style={{ color: voiceGestureHint === 'lock' ? '#007AFF' : '#858E99' }}
                    >
                      {voiceGestureHint === 'lock' ? 'Release to lock' : 'Slide up to lock'}
                    </span>
                  </div>
                )}

                <button
                  ref={micButtonRef}
                  type="button"
                  onPointerDown={handleMicPointerDown}
                  onPointerMove={handleMicPointerMove}
                  onPointerUp={handleMicPointerUp}
                  onPointerCancel={handleMicPointerCancel}
                  onContextMenu={(e) => e.preventDefault()}
                  disabled={isSending || voice.state === 'initializing' || voice.state === 'error'}
                  aria-label={isRecordingVoice ? 'Release to send, slide left to cancel, slide up to lock' : 'Hold to record voice'}
                  className={`rounded-full flex items-center justify-center disabled:opacity-50 transition-all duration-150 ${
                    isRecordingVoice
                      ? 'w-16 h-16 scale-110 shadow-lg shadow-red-500/40'
                      : 'w-9 h-9'
                  }`}
                  style={{
                    background: isRecordingVoice ? '#FF3B30' : 'transparent',
                    color: isRecordingVoice ? '#FFFFFF' : '#858E99',
                    touchAction: 'none',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <Mic
                    className={isRecordingVoice ? 'w-7 h-7' : 'w-[22px] h-[22px]'}
                    strokeWidth={2}
                    aria-hidden
                  />
                </button>
              </div>
            )}

            {/* Locked-recording → tap-to-send. Recording continues hands-free
             *  until tapped (or until the user taps the trash to cancel).
             *  isVoiceLocked reset is mirrored to the user gesture for the
             *  same reason described on the Trash button above — the
             *  state-machine echo is a fallback, not the sole source of truth. */}
            {isVoiceLocked && (
              <button
                type="button"
                onClick={() => {
                  setIsVoiceLocked(false);
                  voice.holdEnd();
                }}
                disabled={isSending || voice.state === 'processing'}
                aria-label="Send voice message"
                className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center touch-active disabled:opacity-50 transition"
                style={{ background: '#007AFF' }}
              >
                {voice.state === 'processing' ? (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <SendHorizontal className="w-[18px] h-[18px] text-white" strokeWidth={2} aria-hidden />
                )}
              </button>
            )}

          </div>
        </div>
      )}

      {/* Closed ticket notice – frosted glass bar */}
      {ticket.status === 'closed' && (
        <div
          className="flex-none z-10 bg-white/80 backdrop-blur-md safe-area-bottom text-center py-3 px-4"
          style={{ borderTop: '0.5px solid rgba(60,60,67,0.36)', color: '#6C6C70' }}
        >
          <span className="text-[15px]">🔒 This ticket is closed</span>
          {ticket.ratingStars && (
            <div className="mt-0.5 text-[13px]">
              Rating: {'⭐'.repeat(ticket.ratingStars)}
            </div>
          )}
          {/* Phase 34: Level-2 reopen is per-ticket via long-press on the
              TICK-XXXXX divider chip (see MessageList wiring below). The
              former bottom-bar "Reopen ticket" link was removed because it
              was ambiguous in user-conversation view (multiple tickets
              stacked) and redundant in single-ticket detail view (the
              divider chip is present there too). */}
        </div>
      )}

      {/* Modals */}
      {showCategoryModal && (
        <CategoryModal
          serviceTypes={serviceTypes}
          categories={categories}
          onClose={() => setShowCategoryModal(false)}
          onConfirm={handleCloseTicket}
          isSending={isSending}
        />
      )}

      {showMediaPicker && (
        <MediaPicker
          onSelect={handleAddAttachment}
          onClose={() => setShowMediaPicker(false)}
        />
      )}

      {/* Phase 37 — canned-responses picker. Inserts the chosen answer's
          body into the composer (insert-only; no auto-send). */}
      <CannedResponsePicker
        open={showCannedPicker}
        onClose={() => setShowCannedPicker(false)}
        onInsert={insertCannedBody}
        /* Phase 38: pass the focused ticket's Phase 36 caseType so the picker
           surfaces the matching category first. Narrow to the two real values
           ('SV'/'WL'); any other/absent value → null (no suggestion). Matches
           on externalCaseType (caseType), NOT the sv_gf_ failed-activity chip. */
        suggestedCaseType={
          ticket.externalCaseType === 'SV' || ticket.externalCaseType === 'WL'
            ? ticket.externalCaseType
            : null
        }
        onManage={onOpenAdmin}
      />

      {/* Phase 34: Level-2 reopen modal. Required reason textarea + Cancel /
          Reopen buttons. On success, refreshes the ticket via the hook so the
          closed-bar disappears and the composer comes back. */}
      {showReopenModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => { if (!reopenSubmitting) setShowReopenModal(false); }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="reopen-modal-title"
        >
          <div
            className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 safe-area-bottom"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="reopen-modal-title" className="text-[17px] font-semibold mb-1">
              Reopen {reopenTargetTicketId ?? ticketId}
            </h2>
            <p className="text-[13px] text-gray-500 mb-3">
              Provide a brief reason — this is recorded with the ticket for audit.
            </p>
            <textarea
              value={reopenReason}
              onChange={(e) => { setReopenReason(e.target.value); setReopenError(null); }}
              disabled={reopenSubmitting}
              placeholder="e.g. user followed up with new information"
              className="w-full border border-gray-300 rounded-lg p-3 text-[15px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              autoFocus
              maxLength={500}
            />
            {reopenError && (
              <p className="mt-2 text-[13px] text-red-600" role="alert">{reopenError}</p>
            )}
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setShowReopenModal(false)}
                disabled={reopenSubmitting}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 text-[15px] font-medium touch-active disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={reopenSubmitting || reopenReason.trim().length === 0}
                onClick={async () => {
                  // Target the explicitly-set ticket when the user
                  // long-pressed a divider in conversation view; otherwise
                  // fall back to props.ticketId (single-ticket-detail mode).
                  const targetId = reopenTargetTicketId ?? ticketId;
                  setReopenSubmitting(true);
                  setReopenError(null);
                  const result = await apiClient.reopenTicket(targetId, reopenReason.trim());
                  setReopenSubmitting(false);
                  if (result.success && result.data) {
                    setShowReopenModal(false);
                    setReopenTargetTicketId(null);
                    // Refresh shared list state so the dashboard / TicketList
                    // shows the reopened ticket in "New Tickets" and removes
                    // it from "Resolved" when the user navigates back later.
                    // (Socket.io stats_updated covers mounted list views; this
                    // is the defensive belt for cached state.)
                    void refreshOpenTickets();
                    void loadStats();
                    if (ticket?.userId) void loadUserActiveTickets(Number(ticket.userId));
                    // Navigate INTO the just-reopened ticket's own
                    // single-ticket detail view. This is what the user wants:
                    // an active-layout chat surface (composer + Close button +
                    // full history) instead of the broken/stale
                    // multi-ticket conversation view they were just looking
                    // at. The reopen route returns the updated Ticket, which
                    // we hand straight to onSelectTicket so App.tsx can seed
                    // the new detail view without a "loading shell" flash.
                    if (onSelectTicket && result.data.ticket) {
                      onSelectTicket(result.data.ticket);
                    } else {
                      // Fallback when no navigation callback was supplied
                      // (embed surfaces etc.): in-place refresh so at least
                      // the divider chip flips to active styling.
                      await beginTicketDetailSession(ticketId);
                    }
                  } else {
                    setReopenError(result.error || 'Failed to reopen ticket');
                  }
                }}
                className="flex-1 py-2.5 rounded-lg text-white text-[15px] font-medium touch-active disabled:opacity-50"
                style={{ background: '#007AFF' }}
              >
                {reopenSubmitting ? 'Reopening…' : 'Reopen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* v4.1 — long-press action modal. Renders a clone of the held bubble
          above a blurred backdrop with a contextual menu hanging directly
          underneath, Telegram-style. Single-step menu: Select / Delete.
          Delete is disabled when the message can't be unsent on Telegram
          (user-sent, missing telegram_message_id, or >48 h old). */}
      {activeDeleteModal && (
        <MessageActionModal
          anchorRect={activeDeleteModal.anchorRect}
          bubbleHtml={activeDeleteModal.bubbleHtml}
          isMineSide={activeDeleteModal.isMine}
          message={activeDeleteModal.message}
          viewerDeviceId={authUser?.deviceId}
          viewerLevel={authUser?.level}
          onReply={handleModalReply}
          onEdit={handleModalEdit}
          onSelect={handleModalSelect}
          onConfirmDelete={handleSingleDeleteConfirm}
          onClose={() => setActiveDeleteModal(null)}
        />
      )}

      {/* v4.1 — batch destructive sheet from the floating trash chip in
          multi-select mode. Single-message deletes use the contextual menu
          inside MessageActionModal (above) instead. */}
      {activeDeleteSheet && (
        <MessageDeleteSheet
          messages={activeDeleteSheet}
          onConfirm={performDelete}
          onClose={() => setActiveDeleteSheet(null)}
        />
      )}

      {/* v4.1 — multi-select action bar. Replaces the input row in selection
          mode and matches its frosted-glass styling so it docks into the
          same chrome slot. Full-width "Delete (N)" button — disabled and
          dimmed when no rows are selected, so the technician can see at
          a glance where Delete will land. Tapping opens the existing
          confirm sheet, which then layers above this bar. */}
      {messageSelectionMode && (
        <div
          className="flex-none z-10 bg-white/80 backdrop-blur-md safe-area-bottom"
          style={{ borderTop: '0.5px solid rgba(60,60,67,0.36)' }}
        >
          <div className="px-3 py-2 max-w-4xl mx-auto">
            <button
              type="button"
              onClick={openDeleteSheetForBatch}
              disabled={selectedMessageIds.size === 0}
              aria-label={`Delete ${selectedMessageIds.size} selected ${
                selectedMessageIds.size === 1 ? 'message' : 'messages'
              }`}
              className="w-full py-3 rounded-2xl bg-white shadow-xl text-center text-base font-medium text-[#FF3B30] disabled:text-gray-400 disabled:cursor-not-allowed touch-active"
            >
              {selectedMessageIds.size > 0
                ? `Delete (${selectedMessageIds.size})`
                : 'Delete'}
            </button>
          </div>
        </div>
      )}

    </div>
    </VoiceSpeedProvider>
  );
}

